/* resolvers */
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import i18n from '@/libs/i18n'
import Vue from 'vue'

export const avatarText = value => {
  if (!value) return ''
  const nameArray = value.split(' ')
  return nameArray.map(word => word.charAt(0).toUpperCase()).join('')
}

export const resolveAvatarVariant = value => {
  const firstLetter = avatarText(value).charCodeAt(0)
  if (firstLetter < 70) return 'primary'
  if (firstLetter < 75) return 'warning'
  if (firstLetter < 80) return 'success'
  if (firstLetter < 85) return 'info'
  if (firstLetter <= 90) return 'danger'
  return 'primary'
}

export const resolveStatusVariant = status => {
  if (!status) return 'primary'
  if (status.toLowerCase() === 'cancellato' || status.toLowerCase() === 'deceduto') return 'danger'
  if (status.toLowerCase() === 'attivo') return 'success'
  if (status.toLowerCase() === 'sospeso') return 'secondary'
  return 'primary'
}

export const resolveTagBackground = (tag, colors) => {
  if (!tag || !colors) return null
  const option = colors.find(t => t.label === tag)
  if (option) return option.color
  return null
}
export const resolveTagIcon = tag => {
  if (!tag) return null
  if (tag.toUpperCase() === 'STOP COMUNICAZIONI') return 'PhoneOffIcon'
  return null
}

export const resolveTaskVariant = status => {
  if (!status) return 'primary'
  if (status.toLowerCase() === 'todo') return 'danger'
  if (status.toLowerCase() === 'waiting') return 'warning'
  if (status.toLowerCase() === 'completed') return 'success'
  if (status.toLowerCase() === 'disabled') return 'secondary'
  return 'primary'
}

export const resolveTaskIcon = status => {
  if (!status) return 'FileTextIcon'
  if (status.toLowerCase() === 'todo') return 'XIcon'
  if (status.toLowerCase() === 'waiting') return 'AlertTriangleIcon'
  if (status.toLowerCase() === 'completed') return 'CheckIcon'
  if (status.toLowerCase() === 'disabled') return 'EyeOffIcon'
  return 'InfoIcon'
}

/* formats */
const moment = require('moment')
const { convert } = require('html-to-text')

export const formatDateHumanFriendly = (value, withHours = true) => {
  if (!value) return value
  moment.locale('it')
  const now = moment(new Date())
  const date = moment(new Date(value))
  const hours = (withHours ? `, ${date.format('HH:mm')}` : '')
  const diffDays = now.startOf('day').diff(date.startOf('day'), 'days')
  if (diffDays === 0) return `oggi${hours}`
  if (diffDays === 1) return `ieri${hours}`
  if (diffDays < 7) return `${date.format('dddd D')}${hours}`
  return date.format('DD MMM yyyy')
}

export const formatDate = (value, pattern) => {
  if (!value || !pattern) return value
  moment.locale('it')
  const date = moment(new Date(value))
  return date.format(pattern)
}

export const isValidYear = year => {
  if (year) {
    const number = parseInt(year, 10)
    return /^\d+$/.test(year) && number >= 1900 && number <= 2100
  }
  return false
}
export const parseDate = (str, pattern) => {
  if (!str || !pattern || str.length < 8) return null
  const result = moment(str, pattern)
  if (result && isValidYear(result.toDate().getFullYear())) return result.add(result.utcOffset(), 'minutes').toDate()
  return null
}

export const formatAge = value => {
  if (!value) return value
  const today = new Date()
  const birthDate = new Date(value)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}

export const formatAgeYear = value => {
  if (!value) return value
  const today = new Date()
  const age = today.getFullYear() - value
  return age
}

export const formatNumber = value => {
  if (!value || value === 0 || value === null) return '-'
  return Intl.NumberFormat('it-IT').format(value)
}
export const formatCurrency = (value, currency) => {
  if (!value || value === 0 || value === null) return '-'
  return `${formatNumber(value)}${currency ? ` ${currency}` : ''}`
}

export const formatAddress = (address, includeAddressLine = false) => {
  if (!address || address === {}) return ''
  return (includeAddressLine ? `${address.addressLine ? `${address.addressLine}, ` : ''}` : '')
  + (address.postcode ? `${address.postcode} - ` : '')
  + (address.city ? address.city : '')
  + (address.kanton ? ` (${address.kanton})` : '')
  + (address.state ? ` | ${address.state}` : '')
}

export const formatHtmlToText = value => convert(value).replace(/\n/g, ' | ')

/* attachments */
export const formatFileType = filename => {
  if (!filename || filename === null) return 'File'
  const extension = filename.split('.').pop().toLowerCase()
  if (extension === 'doc' || extension === 'docx') return 'Documento di Microsoft Word'
  if (extension === 'pdf') return 'Documento PDF'
  return `File ${extension.toUpperCase()}`
}

export const resolveFileIcon = filename => {
  if (!filename || filename === null) return 'Paperclip'
  const extension = filename.split('.').pop().toLowerCase()
  if (extension === 'doc' || extension === 'docx' || extension === 'pdf') return 'FileText'
  if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') return 'Image'
  return 'Paperclip'
}
export const formatBytes = (bytes, decimals) => {
  if (bytes === 0) return '0 bytes'
  const k = 1024
  const dm = decimals || 2
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`
}
export const getResponseFilename = response => {
  const disposition = response.headers['content-disposition']
  let filename = 'unknown'
  if (disposition && disposition.indexOf('attachment') !== -1) {
    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition)
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '')
    }
  }
  return filename
}
export const download = (response, rename) => {
  const blob = new Blob([response.data], { type: response.data.type })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const filename = (rename || getResponseFilename(response))

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

/* utils */
export const clone = obj => JSON.parse(JSON.stringify(obj))
export const decode = data => { const enc = new TextDecoder('utf-8'); return enc.decode(data) }

export const removeFromArray = (array, item) => {
  for (let i = 0; i < array.length; i += 1) {
    if (array[i] === item) {
      array.splice(i, 1)
      i += 1
    }
  }
}

export const swalCloseOptions = {
  title: i18n.t('common.save-warning'),
  text: i18n.t('common.close-question'),
  showCancelButton: true,
  confirmButtonText: i18n.t('common.close'),
  cancelButtonText: i18n.t('common.cancel'),
  customClass: {
    confirmButton: 'btn btn-outline-primary',
    cancelButton: 'btn btn-outline-danger ml-1',
  },
  buttonsStyling: false,
}

export const swalSaveOptions = {
  title: i18n.t('common.save-warning'),
  text: i18n.t('common.save-question'),
  showCancelButton: true,
  showDenyButton: true,
  confirmButtonText: i18n.t('common.save-ok'),
  denyButtonText: i18n.t('common.save-deny'),
  cancelButtonText: i18n.t('common.cancel'),
  customClass: {
    confirmButton: 'btn btn-outline-primary',
    cancelButton: 'btn btn-outline-danger ml-1',
    denyButton: 'btn btn-outline-primary ml-1',
  },
  buttonsStyling: false,
}

export const swalDeleteOptions = {
  title: i18n.t('common.delete-warning'),
  text: i18n.t('common.delete-warning2'),
  showCancelButton: true,
  confirmButtonText: i18n.t('common.delete-ok'),
  cancelButtonText: i18n.t('common.cancel'),
  customClass: {
    confirmButton: 'btn btn-outline-primary',
    cancelButton: 'btn btn-outline-danger ml-1',
  },
  buttonsStyling: false,
}

export const swalCustomOptions = (title, warning) => ({
  title: i18n.t(title),
  text: i18n.t(warning),
  showCancelButton: true,
  confirmButtonText: i18n.t('common.confirm'),
  cancelButtonText: i18n.t('common.cancel'),
  customClass: {
    confirmButton: 'btn btn-outline-primary',
    cancelButton: 'btn btn-outline-danger ml-1',
  },
  buttonsStyling: false,
})

export const toastSuccessOptions = {
  component: ToastificationContent,
  props: {
    title: i18n.t('common.save'),
    icon: 'CheckIcon',
    text: i18n.t('common.save-success'),
    variant: 'success',
  },
}

export const toastErrorOptions = {
  component: ToastificationContent,
  props: {
    title: i18n.t('common.error'),
    icon: 'AlertCircleIcon',
    variant: 'danger',
  },
}

export const throwError = error => {
  const message = toastErrorOptions
  if (error.message)message.props.text = error.message
  else if (error.response) message.props.text = decode(error.response.data)
  Vue.$toast(message, { position: 'top-center' })
}

export const quillEditorOptions = {
  placeholder: '',
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'], // toggled buttons
      // ['blockquote', 'code-block'],
      // [{ header: 1 }, { header: 2 }], // custom button values
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
      [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
      // [{ direction: 'rtl' }], // text direction
      [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
      // [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }], // dropdown with defaults from theme
      // [{ font: [] }],
      [{ align: [] }],
      // ['clean'],
    ],
  },
}

export const scrollTo = elementId => {
  setTimeout(() => {
    let element = document.getElementById(elementId)
    let top = 0
    do {
      top += element.offsetTop || 0
      element = element.offsetParent
    } while (element)

    window.scrollTo(0, top - 90) // adding navbar height
  }, 100)
}
